import {
  CONNECT_APP_NAMES,
  DEVICE_BRAND_NAME_OVERRIDES,
} from '@constants/device-brands';

import type { DataPointMapped } from '@models/data-point';

import { capitalize } from '@utils/format';

import { ResourceSourceType } from 'generated/types';

/** A Terra provider slug is a single lowercase alphanumeric token. */
const PROVIDER_SLUG = /^[a-z0-9]+$/;

/**
 * Whether a source string is a Terra provider slug rather than a free-text
 * name.
 *
 * `Observation.device.display` holds a slug for device data, but the same
 * field carries a device name from the health metrics mapper and
 * `ObservationRecord.source` falls back to `performer[].display`, which is a
 * practitioner name. Only a slug can be turned into an app tile key.
 * @param source `Observation.device.display`
 * @returns True when the value is a provider slug
 */
export function isProviderSlug(source?: string | null) {
  const value = source?.trim();
  return !!value && PROVIDER_SLUG.test(value);
}

/**
 * Whether any reading in the set came from a connected health device.
 *
 * The attribution line names the reader's connected devices, so it only
 * belongs on a view that is actually showing device readings. Categories are
 * too blunt a test: Body holds scale readings alongside manually entered
 * weights, and vital signs hold Garmin heart rate alongside clinic readings.
 * @param records Data points backing the view
 * @returns True when at least one reading is device sourced
 */
export function hasDeviceSourcedData(records: DataPointMapped[]) {
  return records.some(
    ({ record }) => record.sourceType === ResourceSourceType.device
  );
}

/**
 * Display name for the brand a reading came from.
 *
 * `Observation.device.display` holds the lowercased Terra provider slug for
 * device data, but the health metrics mapper writes a device name into the
 * same field, so anything that is not a slug is passed through untouched
 * rather than re-cased.
 * @param source `Observation.device.display`
 * @returns Brand display name, or undefined when there is no source
 */
export function getDeviceBrandName(source?: string | null) {
  const value = source?.trim();
  if (!value) {
    return undefined;
  }
  if (!isProviderSlug(value)) {
    return value;
  }
  return DEVICE_BRAND_NAME_OVERRIDES[value] ?? capitalize(value);
}

/**
 * Product name to show for a provider on a connect surface.
 * @param integration `TerraIntegration.integration`
 * @param name Display name supplied by Terra
 * @returns The required product name, falling back to Terra's own
 */
export function getConnectAppName(integration: string, name: string) {
  return CONNECT_APP_NAMES[integration] ?? name;
}
